import React, { useEffect, useState } from "react";
import Select from "react-select";
import api from "../axiosConfig";
import {
  explorationLevelHelperText,
  explorationSliderFillPercent,
  explorationSliderLabelClass,
} from "../utils/explorationLevelHelperText";
import "../styles/explorationSlider.css";

const stringsToOptions = (arr) =>
  (arr || []).map((s) => ({ value: s, label: s }));

/** Site teal — matches buttons / focus rings elsewhere (not react-select default blue). */
const TEAL = "#0196BF";

const recommendationFilterSelectTheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: TEAL,
    primary25: "rgba(1, 150, 191, 0.12)",
    primary50: "rgba(1, 150, 191, 0.2)",
    primary75: "rgba(1, 150, 191, 0.35)",
  },
});

const recommendationFilterSelectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? TEAL : base.borderColor,
    boxShadow: state.isFocused
      ? "0 0 0 2px rgba(1, 150, 191, 0.35)"
      : base.boxShadow,
    "&:hover": {
      borderColor: TEAL,
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? TEAL
      : state.isFocused
        ? "rgba(1, 150, 191, 0.12)"
        : base.backgroundColor,
    color: state.isSelected ? "white" : base.color,
    "&:active": {
      backgroundColor: state.isSelected
        ? TEAL
        : "rgba(1, 150, 191, 0.22)",
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#f3f4f6",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#333",
  }),
  multiValueRemove: (base) => ({
    ...base,
    "&:hover": {
      backgroundColor: "rgba(1, 150, 191, 0.4)",
      color: "white",
    },
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 220,
    paddingTop: 4,
    paddingBottom: 8,
  }),
};

const Settings = ({ isOpen, closeSettings }) => {
  const [allowSchoolContact, setAllowSchoolContact] = useState(false);
  const [explorationLevel, setExplorationLevel] = useState(3);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [majorOptions, setMajorOptions] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [majorFilters, setMajorFilters] = useState([]);
  const [schoolFilters, setSchoolFilters] = useState([]);
  const [filtersSaving, setFiltersSaving] = useState(false);
  const [filtersError, setFiltersError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setPrefsLoading(true);
    setFiltersError("");
    const load = async () => {
      try {
        const [prefsRes, majorsRes, schoolsRes] = await Promise.all([
          api.get("/user-preferences/"),
          api.get("/get-program-disciplines/"),
          api.get("/get-school-names/"),
        ]);
        if (cancelled) return;
        const data = prefsRes.data;
        setAllowSchoolContact(!!data.allow_school_contact);
        const el = data.recommendation_exploration_level;
        if (typeof el === "number" && el >= 1 && el <= 5) {
          setExplorationLevel(el);
        } else {
          setExplorationLevel(3);
        }
        setMajorFilters(stringsToOptions(data.interested_majors));
        setSchoolFilters(stringsToOptions(data.interested_schools));

        const mo = majorsRes.data.program_disciplines.map((d) => ({
          value: d,
          label: d,
        }));
        const so = schoolsRes.data.school_names.map((s) => ({
          value: s,
          label: s,
        }));
        setMajorOptions(mo);
        setSchoolOptions(so);
      } catch (e) {
        if (!cancelled) setFiltersError("Could not load settings.");
      } finally {
        if (!cancelled) setPrefsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleExplorationChange = async (next) => {
    setExplorationLevel(next);
    try {
      await api.patch("/user-preferences/", {
        recommendation_exploration_level: next,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggleSchoolContact = async () => {
    const next = !allowSchoolContact;
    try {
      await api.patch("/user-preferences/", { allow_school_contact: next });
      setAllowSchoolContact(next);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveFilters = async () => {
    setFiltersError("");
    setFiltersSaving(true);
    try {
      await api.patch("/user-preferences/", {
        interested_majors: majorFilters.map((o) => o.value),
        interested_schools: schoolFilters.map((o) => o.value),
      });
      window.dispatchEvent(new Event("gradconnect:filters-updated"));
    } catch (e) {
      console.error(e);
      setFiltersError(
        e.response?.data?.error || "Could not save filters. Try again."
      );
    } finally {
      setFiltersSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-3 sm:p-4">
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-lg bg-white p-4 sm:p-6">
        <button
          onClick={closeSettings}
          className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 sm:right-4 sm:top-4"
          type="button"
          aria-label="Close settings"
        >
          <span className="material-icons text-2xl">close</span>
        </button>

        <h2 className="text-center text-xl font-semibold sm:text-2xl pr-10 mb-5 sm:mb-6">
          Settings
        </h2>

        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
          <p className="mb-1 text-left text-sm font-semibold text-gray-900">
            Focus vs. explore
          </p>
          <p className="mb-1 text-left text-sm text-gray-600">
            If you know what you want to study, choose Focus.
          </p>
          <p className="mb-3 text-left text-sm text-gray-600">
            If you&apos;re still exploring, choose Explore.
          </p>
          <div className="mb-0 flex items-center justify-between leading-none text-[10px] font-medium sm:text-[11px]">
            <span className={explorationSliderLabelClass(explorationLevel, "focus")}>
              Focus
            </span>
            <span className={explorationSliderLabelClass(explorationLevel, "explore")}>
              Explore
            </span>
          </div>
          <input
            type="range"
            min={1}
            max={5}
            step={1}
            value={explorationLevel}
            onChange={(e) => handleExplorationChange(Number(e.target.value))}
            disabled={prefsLoading}
            className="exploration-range -mt-3 w-full"
            style={{
              "--fill-pct": `${explorationSliderFillPercent(explorationLevel)}%`,
            }}
            aria-label="Recommendation mix from Focus to Explore"
            aria-valuemin={1}
            aria-valuemax={5}
            aria-valuenow={explorationLevel}
          />
          <p className="-mt-1.5 text-center text-xs leading-snug text-gray-500">
            {explorationLevelHelperText(explorationLevel)}
          </p>
        </div>

        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
          <p className="mb-1 text-left text-sm font-semibold text-gray-900">
            Recommendation filters
          </p>
          <p className="mb-4 text-left text-sm text-gray-600">
            Narrow results by discipline or school.
          </p>
          <div className="mb-3 text-left">
            <p className="mb-0 text-left text-sm font-semibold leading-none text-gray-900">
              Disciplines
            </p>
            <p className="mt-0 mb-1 text-left text-sm leading-snug text-gray-600">
              Select the departments you&apos;d consider.
            </p>
            <Select
              id="settings-major-select"
              isMulti
              options={majorOptions}
              value={majorFilters}
              onChange={setMajorFilters}
              placeholder="All disciplines"
              className="text-left"
              classNamePrefix="select"
              theme={recommendationFilterSelectTheme}
              styles={recommendationFilterSelectStyles}
              isDisabled={prefsLoading || filtersSaving}
              aria-label="Disciplines for recommendations"
            />
          </div>
          <div className="mb-2 text-left">
            <p className="mb-0 text-left text-sm font-semibold leading-none text-gray-900">
              Schools
            </p>
            <p className="mt-0 mb-1 text-left text-sm leading-snug text-gray-600">
              Leave blank to see all schools.
            </p>
            <Select
              id="settings-school-select"
              isMulti
              options={schoolOptions}
              value={schoolFilters}
              onChange={setSchoolFilters}
              placeholder="All schools"
              className="text-left"
              classNamePrefix="select"
              theme={recommendationFilterSelectTheme}
              styles={recommendationFilterSelectStyles}
              isDisabled={prefsLoading || filtersSaving}
              aria-label="Schools for recommendations"
            />
          </div>
          {filtersError ? (
            <p className="mb-2 text-left text-sm text-red-600">{filtersError}</p>
          ) : null}
          <button
            type="button"
            onClick={handleSaveFilters}
            disabled={prefsLoading || filtersSaving}
            className="w-full rounded-lg bg-[#0196BF] px-4 py-2 text-sm font-medium text-white hover:bg-[#017BA3] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {filtersSaving ? "Saving…" : "Save filters"}
          </button>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
          <div className="flex min-w-0 flex-col gap-3 nav:flex-row nav:items-start nav:justify-between nav:gap-4">
            <div className="min-w-0 w-full text-left text-sm text-gray-700 nav:flex-1">
              <span
                id="school-contact-settings-label"
                className="mb-1 block font-semibold text-gray-900"
              >
                Share my info with programs I&apos;m interested in
              </span>
              When you save a professor, we may share your name and email with
              their program or department so they can contact you.
            </div>
            <div className="flex shrink-0 justify-end nav:mt-0.5 nav:justify-end">
              <button
                type="button"
                role="switch"
                aria-checked={allowSchoolContact}
                aria-labelledby="school-contact-settings-label"
                disabled={prefsLoading}
                onClick={handleToggleSchoolContact}
                className={[
                  "relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0196BF] focus-visible:ring-offset-2",
                  allowSchoolContact ? "bg-[#0196BF]" : "bg-gray-300",
                  prefsLoading ? "cursor-not-allowed opacity-50" : "",
                ].join(" ")}
              >
                <span
                  className={[
                    "pointer-events-none block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ease-out",
                    allowSchoolContact ? "translate-x-5" : "translate-x-0",
                  ].join(" ")}
                  aria-hidden
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
